import axios from 'axios';
import { useAtom, useSetAtom } from 'jotai';
import { environment } from '../../environment';
import { errorCatcher, useSnackbar } from '../../hooks';
import { useLogin } from '../login';
import { IAuditGroup, IAuditGroupInput, auditGroupsAtom, auditGroupsLoadingAtom } from './types';

type UseAuditGroups = [IAuditGroup[], {
  list: (query?: Record<string, any>) => Promise<IAuditGroup[] | null>;
  getById: (id: string) => Promise<IAuditGroup | null>;
  getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IAuditGroup[] | null>;
  create: (input: IAuditGroupInput) => Promise<IAuditGroup | null>;
  update: (id: string, input: IAuditGroupInput) => Promise<IAuditGroup | null>;
  remove: (id: string) => Promise<IAuditGroup | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
  const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
  if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
  return data.data;
}

export const useAuditGroups = (): UseAuditGroups => {
  const [items, setItems] = useAtom(auditGroupsAtom);
  const setLoading = useSetAtom(auditGroupsLoadingAtom);
  const [, setSnackbar] = useSnackbar();
  const [, authToken, loginFunctions] = useLogin();

  const list = async (query: Record<string, any> = {}) => {
    try { setLoading(true);
      const q = `query($query: JSON){ getTrackableItemAuditGroups(query: $query){ id name created_at updated_at } }`;
      const data = await graphqlRequest(authToken, q, { query });
      setItems(data.getTrackableItemAuditGroups);
      return data.getTrackableItemAuditGroups as IAuditGroup[];
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch audit groups.'}); return null; }
    finally { setLoading(false);} };

  const getById = async (id: string) => {
    try { const q = `query($id: ID!){ getTrackableItemAuditGroupById(id:$id){ id name created_at updated_at } }`;
      const data = await graphqlRequest(authToken, q, { id });
      return data.getTrackableItemAuditGroupById as IAuditGroup;
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
  };

  const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
    try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedTrackableItemAuditGroups(first:$first,offset:$offset,query:$query){ id name created_at updated_at } }`;
      const data = await graphqlRequest(authToken, q, { first, offset, query });
      return data.getPaginatedTrackableItemAuditGroups as IAuditGroup[];
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
  };

  const create = async (input: IAuditGroupInput) => {
    try { const q = `mutation($input:TrackableItemAuditGroupInput!){ createTrackableItemAuditGroup(input:$input){ id } }`;
      const data = await graphqlRequest(authToken, q, { input });
      return data.createTrackableItemAuditGroup as IAuditGroup;
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
  };

  const update = async (id: string, input: IAuditGroupInput) => {
    try { const q = `mutation($id:ID!,$input:TrackableItemAuditGroupInput!){ updateTrackableItemAuditGroup(id:$id,input:$input){ id } }`;
      const data = await graphqlRequest(authToken, q, { id, input });
      return data.updateTrackableItemAuditGroup as IAuditGroup;
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
  };

  const remove = async (id: string) => {
    try { const q = `mutation($id:ID!){ deleteTrackableItemAuditGroup(id:$id){ id } }`;
      const data = await graphqlRequest(authToken, q, { id });
      return data.deleteTrackableItemAuditGroup as IAuditGroup;
    } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
  };

  return [items, { list, getById, getPaginated, create, update, remove }];
}


